/**
 * Keep Warm queue engine: eligibility, due date, state (cooling / due_now), labels.
 *
 * - Lead is eligible if: open (not won/lost/archived), has lastOutboundAt, and
 *   (no lastInboundAt or lastInboundAt < lastOutboundAt).
 * - dueAt = keep_warm_snoozed_until if > now, else keep_warm_due_at ?? lastOutboundAt + KEEP_WARM_DELAY.
 * - state = "due_now" when now >= dueAt, else "cooling".
 */

import { getKeepWarmDelayMs } from './inactionConfig';
import { INACTION_COPY } from './inactionCopy';

export interface KeepWarmLeadInput {
  created_at?: string | null;
  last_contacted_at?: string | null;
  last_message_at?: string | null;
  last_interaction_at?: string | null;
  last_inbound_at?: string | null;
  pipeline_stage?: string | null;
  archived?: boolean | null;
  keep_warm_due_at?: string | null;
  keep_warm_snoozed_until?: string | null;
}

export type KeepWarmState = 'cooling' | 'due_now';

export interface KeepWarmResult {
  lastOutboundAt: Date | null;
  lastInboundAt: Date | null;
  isEligible: boolean;
  dueAt: Date | null;
  state: KeepWarmState;
  timeSinceOutboundMs: number;
  stateLabel: string;
  dueInLabel: string | null;
  overdueByLabel: string | null;
  /** For UI: e.g. "Waiting for reply • 2d ago" */
  timeSinceOutboundLabel: string | null;
}

function parseDate(value: string | Date | null | undefined): Date | null {
  if (!value) return null;
  const d = typeof value === 'string' ? new Date(value) : value;
  return Number.isNaN(d.getTime()) ? null : d;
}

function formatDuration(ms: number): string {
  const totalSeconds = Math.max(0, Math.floor(ms / 1000));
  const totalMinutes = Math.floor(totalSeconds / 60);
  const hours = Math.floor(totalMinutes / 60);
  const days = Math.floor(hours / 24);
  const minutes = totalMinutes % 60;

  if (days > 0) return `${days}d`;
  if (hours > 0) {
    if (minutes === 0) return INACTION_COPY.duration.formatHours(hours);
    return INACTION_COPY.duration.formatHoursMinutes(hours, minutes);
  }
  if (minutes > 0) return INACTION_COPY.duration.formatMinutes(minutes);
  return INACTION_COPY.duration.fewMinutes;
}

/**
 * Compute Keep Warm state for a lead.
 *
 * - lastOutboundAt: last_contacted_at ?? last_message_at ?? last_interaction_at
 * - isEligible: lead open AND lastOutboundAt exists AND (lastInboundAt is null OR lastInboundAt < lastOutboundAt)
 * - dueAt: keep_warm_snoozed_until > now ? that : (keep_warm_due_at ?? lastOutboundAt + KEEP_WARM_DELAY)
 * - state: "due_now" if now >= dueAt, else "cooling"
 */
export function computeKeepWarm(
  lead: KeepWarmLeadInput,
  now: Date = new Date()
): KeepWarmResult {
  const nowMs = now.getTime();

  const lastOutboundAt =
    parseDate(lead.last_contacted_at) ??
    parseDate(lead.last_message_at) ??
    parseDate(lead.last_interaction_at) ??
    null;

  const lastInboundAt = parseDate(lead.last_inbound_at) ?? null;

  const isOpen =
    lead.pipeline_stage !== 'won' &&
    lead.pipeline_stage !== 'lost' &&
    (lead.archived === false || lead.archived === null || lead.archived === undefined);

  const isEligible =
    isOpen &&
    lastOutboundAt != null &&
    (lastInboundAt == null || lastInboundAt.getTime() < lastOutboundAt.getTime());

  if (!lastOutboundAt) {
    return {
      lastOutboundAt: null,
      lastInboundAt,
      isEligible: false,
      dueAt: null,
      state: 'cooling',
      timeSinceOutboundMs: 0,
      stateLabel: INACTION_COPY.keepWarm.stateCooling,
      dueInLabel: null,
      overdueByLabel: null,
      timeSinceOutboundLabel: null,
    };
  }

  const delayMs = getKeepWarmDelayMs();
  const keepWarmDueAt = parseDate(lead.keep_warm_due_at);
  const snoozedUntil = parseDate(lead.keep_warm_snoozed_until);

  let dueAt: Date;
  if (snoozedUntil != null && snoozedUntil.getTime() > nowMs) {
    dueAt = snoozedUntil;
  } else if (keepWarmDueAt != null) {
    dueAt = keepWarmDueAt;
  } else {
    dueAt = new Date(lastOutboundAt.getTime() + delayMs);
  }

  const state: KeepWarmState = nowMs >= dueAt.getTime() ? 'due_now' : 'cooling';
  const timeSinceOutboundMs = nowMs - lastOutboundAt.getTime();

  let dueInLabel: string | null = null;
  let overdueByLabel: string | null = null;
  if (nowMs >= dueAt.getTime()) {
    overdueByLabel = formatDuration(nowMs - dueAt.getTime());
  } else {
    dueInLabel = formatDuration(dueAt.getTime() - nowMs);
  }

  const stateLabel =
    state === 'due_now' ? INACTION_COPY.keepWarm.stateDueNow : INACTION_COPY.keepWarm.stateCooling;
  const timeSinceOutboundLabel =
    timeSinceOutboundMs >= 0 ? formatDuration(timeSinceOutboundMs) : null;

  return {
    lastOutboundAt,
    lastInboundAt,
    isEligible: isEligible && !!lastOutboundAt,
    dueAt,
    state,
    timeSinceOutboundMs,
    stateLabel,
    dueInLabel,
    overdueByLabel,
    timeSinceOutboundLabel,
  };
}
